/**
 * CM-API `POST /v1/transfer-statement/search` → Account Statement table rows.
 * Shared DB only — no Agent-API / analytics proxy.
 */

export const LAB_TRANSFER_STATEMENT_MAX_DAYS = 15

export type TransferStatementParty = { id: string; username: string }

export type TransferStatementRow = {
  id: string
  createdAt: string
  amount: number
  transactionType: 'Given' | 'Taken'
  fromUser: TransferStatementParty
  toUser: TransferStatementParty
  comments: string | null
  ip: string | null
}

export type TransferStatementResult = {
  rows: TransferStatementRow[]
  total: number
  page: number
  pageSize: number
  viewer: { id: string; username: string; currencyType: string }
}

function asParty(raw: unknown): TransferStatementParty {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const id = typeof o.id === 'string' ? o.id : ''
  const username = typeof o.username === 'string' && o.username.trim() ? o.username : id.slice(0, 8) || '—'
  return { id, username }
}

function asRow(raw: unknown): TransferStatementRow | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const type = o.transactionType === 'Given' || o.transactionType === 'Taken' ? o.transactionType : null
  if (!type) return null
  const amount = typeof o.amount === 'number' ? o.amount : Number(o.amount)
  if (!Number.isFinite(amount)) return null
  return {
    id: typeof o.id === 'string' ? o.id : `${type}:${o.createdAt}`,
    createdAt: typeof o.createdAt === 'string' ? o.createdAt : '',
    amount,
    transactionType: type,
    fromUser: asParty(o.fromUser),
    toUser: asParty(o.toUser),
    comments: typeof o.comments === 'string' ? o.comments : null,
    ip: typeof o.ip === 'string' ? o.ip : null,
  }
}

export function transferStatementFromLive(live: Record<string, unknown>): TransferStatementResult {
  const resultsRaw = Array.isArray(live.results) ? live.results : []
  const rows = resultsRaw.map(asRow).filter((r): r is TransferStatementRow => r != null)
  const viewerRaw =
    live.viewer && typeof live.viewer === 'object'
      ? (live.viewer as Record<string, unknown>)
      : {}
  return {
    rows,
    total: typeof live.total === 'number' ? live.total : rows.length,
    page: typeof live.page === 'number' ? live.page : 1,
    pageSize: typeof live.size === 'number' ? live.size : rows.length || 20,
    viewer: {
      id: typeof viewerRaw.id === 'string' ? viewerRaw.id : '',
      username:
        typeof viewerRaw.username === 'string' && viewerRaw.username.trim()
          ? viewerRaw.username
          : 'you',
      currencyType:
        typeof viewerRaw.currencyType === 'string' && viewerRaw.currencyType.trim()
          ? viewerRaw.currencyType
          : 'INR',
    },
  }
}

export function emptyTransferStatement(
  page = 1,
  pageSize = 20,
): TransferStatementResult {
  return {
    rows: [],
    total: 0,
    page,
    pageSize,
    viewer: { id: '', username: 'you', currencyType: 'INR' },
  }
}

export function transferStatementRangeDays(fromDay: string, toDay: string): number {
  const from = Date.parse(`${fromDay.slice(0, 10)}T00:00:00`)
  const to = Date.parse(`${toDay.slice(0, 10)}T00:00:00`)
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 0
  return Math.ceil(Math.abs(to - from) / 86_400_000)
}
